import { router, usePage, usePoll } from '@inertiajs/vue3'
import { computed, onBeforeUnmount, onMounted, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * RED-317 §8 — the generation panel's one button, "Loo universaalsed mallid
 * uuesti", and its running state.
 *
 * The truth is the `generation` Inertia prop: `running` stays true from the
 * click (the server creates the run row `queued` before the job is queued)
 * until the job closes the row. While it is true the page partial-reloads
 * `generation` + `overviews` every few seconds, so the report and the matrix
 * update the moment the run ends; a reload mid-run picks the poll back up.
 * The run is also re-read when the tab comes back into sight, because
 * browsers throttle timers in hidden tabs.
 *
 * A refused click (a run started minutes ago) comes back as a validation
 * error and is shown under the button: no toast.
 */
export function useMarketOverviewGeneration() {
  const POLL_INTERVAL_MS = 3000

  const page = usePage()
  const { t } = useI18n()

  const generation = computed(() => page.props.generation ?? { running: false, run: null })
  const run = computed(() => generation.value.run)
  const running = computed(() => generation.value.running === true)
  const submitting = shallowRef(false)
  const error = shallowRef(null)

  const { start: startPoll, stop: stopPoll } = usePoll(
    POLL_INTERVAL_MS,
    { only: ['generation', 'overviews'] },
    { autoStart: false, keepAlive: true },
  )

  watch(running, isRunning => (isRunning ? startPoll() : stopPoll()))

  const resync = () => {
    if (document.visibilityState === 'visible' && running.value) {
      router.reload({ only: ['generation', 'overviews'] })
    }
  }

  onMounted(() => {
    if (running.value) {
      startPoll()
    }
    document.addEventListener('visibilitychange', resync)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', resync)
    stopPoll()
  })

  const busy = computed(() => submitting.value || running.value)

  /** The button's own text: what the run is doing, only while it runs. */
  const buttonLabel = computed(() => {
    if (run.value?.status === 'queued' && running.value) {
      return t('messages.admin.market_overviews.generation.button_queued')
    }
    if (busy.value) {
      return t('messages.admin.market_overviews.generation.button_running')
    }
    return t('messages.admin.market_overviews.generation.button')
  })

  const generate = () => {
    if (busy.value) {
      return
    }
    error.value = null
    submitting.value = true
    router.post(
      route('admin.market-overview.generate'),
      {},
      {
        preserveScroll: true,
        onError: errors => {
          error.value = errors.generate ?? t('messages.admin.market_overviews.generation.failed_to_start')
        },
        onFinish: () => {
          submitting.value = false
        },
      },
    )
  }

  return { run, running, busy, buttonLabel, error, generate }
}
